import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../api';

const SAVE_DEBOUNCE_MS = 500;
const storageKey = (username) => `tc_prefs_${username}`;

export const getDefaultPrefs = () => ({
  filterCategory: '',
  filterClient: '',
  filterRequestedBy: '',
  filterUser: '',
  filterDepartment: '',
  filterYear: new Date().getFullYear().toString(), // default only; a saved preference wins
  filterMonth: '',
  // Projects page filters (kept apart from Time Entry's); the year defaults to the current one, a saved value wins.
  projectsFilterYear: new Date().getFullYear().toString(),
  projectsFilterMonth: '',
  projectsFilterClient: '',
  projectsFilterDept: '',
  sortBy: 'dateModified',
  sortDir: 'desc',
});

const PREF_KEYS = Object.keys(getDefaultPrefs());
const pickKnown = (obj) => {
  const out = {};
  PREF_KEYS.forEach((k) => { if (obj && typeof obj[k] === 'string') out[k] = obj[k]; });
  return out;
};

function readLocal(username) {
  try { return pickKnown(JSON.parse(localStorage.getItem(storageKey(username)) || '{}')); } catch { return {}; }
}

// Filters + sort state, persisted per user: loaded from /api/users/:username/preferences on mount
// (localStorage fallback if the request fails), saved with a 500 ms debounce on every change.
export default function usePersistedFilters(username) {
  const [prefs, setPrefs] = useState(getDefaultPrefs);
  const [loaded, setLoaded] = useState(false);
  const timer = useRef(null);
  // Keys the user changed before the saved preferences arrived. The (possibly slow) load must
  // never overwrite them — otherwise a filter picked during a cold start silently snaps back.
  const touched = useRef(new Set());

  useEffect(() => {
    if (!username) return undefined;
    let cancelled = false;
    setLoaded(false);
    axios
      .get(`${API_BASE_URL}/api/users/${encodeURIComponent(username)}/preferences`, { params: { username } })
      .then((res) => pickKnown(res.data))
      .catch(() => readLocal(username))
      .then((saved) => {
        if (cancelled) return;
        setPrefs((current) => {
          const merged = { ...getDefaultPrefs(), ...saved };
          touched.current.forEach((k) => { merged[k] = current[k]; });
          return merged;
        });
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [username]);

  // Debounced save — never before the saved state has been loaded (would overwrite it with defaults).
  useEffect(() => {
    if (!loaded || !username) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try { localStorage.setItem(storageKey(username), JSON.stringify(prefs)); } catch { /* ignore */ }
      axios
        .put(`${API_BASE_URL}/api/users/${encodeURIComponent(username)}/preferences`, { user: username, preferences: prefs })
        .catch(() => { /* localStorage copy above is the fallback */ });
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
  }, [prefs, loaded, username]);

  const setPref = useCallback((key, value) => {
    touched.current.add(key);
    setPrefs((p) => ({ ...p, [key]: value }));
  }, []);

  // Reset clears the Time Entry filters back to defaults but keeps the chosen sorting
  // (and leaves the Projects page filters alone).
  const resetFilters = useCallback(() => {
    const keys = PREF_KEYS.filter((k) => !k.startsWith('projects'));
    keys.forEach((k) => touched.current.add(k));
    setPrefs((p) => {
      const defaults = getDefaultPrefs();
      const next = { ...p };
      keys.forEach((k) => { next[k] = defaults[k]; });
      next.sortBy = p.sortBy;
      next.sortDir = p.sortDir;
      return next;
    });
  }, []);

  const activeFilterKeys = useMemo(() => {
    const defaults = getDefaultPrefs();
    return PREF_KEYS.filter((k) => k.startsWith('filter') && prefs[k] !== defaults[k]);
  }, [prefs]);

  return { prefs, setPref, resetFilters, loaded, activeFilterKeys };
}
